import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useApp } from "../contexts/AppContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Shield, Flame, HeartPulse, UtensilsCrossed, AlertTriangle, Users,
  Plus, Pencil, Trash2, Save, Calendar, Clock, ChevronRight, FileText,
  Upload, Download, Eye, FolderOpen, Building2, Phone, Siren, BookOpen
} from "lucide-react";
import { API_URL } from "../lib/utils";
import { Textarea } from "../components/ui/textarea";

const CERT_TYPES = {
  antincendio: "Antincendio",
  primo_soccorso: "Primo Soccorso",
  haccp: "HACCP",
  sicurezza_generale: "Sicurezza 81/08 - Generale",
  sicurezza_specifica: "Sicurezza 81/08 - Specifica",
  sicurezza_aggiornamento: "Sicurezza 81/08 - Aggiornamento",
};

const EMERGENCY_TYPES = {
  incendio: "Incendio",
  allagamento: "Allagamento",
  fuga_gas: "Fuga Gas",
  blackout: "Blackout Elettrico",
  emergenza_sanitaria: "Emergenza Sanitaria",
  evacuazione: "Evacuazione",
  infestazioni: "Infestazioni",
};

const LOC_DOC_CATEGORIES = {
  antincendio: { label: "Antincendio", subcategories: ["SCIA", "CPI", "Verbali VVF", "Certificazioni impianti"] },
  planimetrie: { label: "Planimetrie", subcategories: ["Planimetria generale", "Planimetria emergenza", "Vie di fuga"] },
  licenze: { label: "Licenze", subcategories: ["SCIA attività", "Licenze comunali", "Autorizzazioni sanitarie"] },
  contratti: { label: "Contratti", subcategories: ["Manutenzioni", "Vigilanza", "Assicurazioni", "Smaltimento rifiuti"] },
  legionella: { label: "Legionella", subcategories: ["DVR Legionella", "Analisi acqua", "Verbali campionamento"] },
  sicurezza_81: { label: "Sicurezza D.Lgs. 81/08", subcategories: ["DVR", "DUVRI", "Piano Emergenza", "Nomine RSPP", "Nomine Addetti", "Verbali Riunioni"] },
  pest_control: { label: "Pest Control", subcategories: ["Contratto", "Planimetria esche", "Report interventi", "Certificazioni"] },
};

function StatusDot({ status }) {
  const colors = {
    green: "bg-green-500",
    yellow: "bg-yellow-400",
    red: "bg-red-500",
    grey: "bg-gray-300",
  };
  return <div className={`w-3 h-3 rounded-full ${colors[status] || colors.grey}`} title={status} />;
}

function StatusBadge({ status, text }) {
  const styles = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    grey: "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium ${styles[status] || styles.grey}`}>
      <StatusDot status={status} />
      {text}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "-";
  const p = d.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ data, onSelectLocation }) {
  if (!data) return <div className="py-8 text-center text-[#71717A] font-ibm-plex">Caricamento...</div>;

  const { locations, alerts } = data;

  return (
    <div className="space-y-6">
      {/* Alert summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold font-ibm-plex-mono text-red-600">{alerts.expired}</p>
            <p className="text-xs text-red-600 font-ibm-plex">Scaduti</p>
          </CardContent>
        </Card>
        <Card className="border border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold font-ibm-plex-mono text-yellow-600">{alerts.expiring_soon}</p>
            <p className="text-xs text-yellow-600 font-ibm-plex">In scadenza</p>
          </CardContent>
        </Card>
        <Card className="border border-orange-200 bg-orange-50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold font-ibm-plex-mono text-orange-600">{alerts.missing_staff}</p>
            <p className="text-xs text-orange-600 font-ibm-plex">Addetti mancanti</p>
          </CardContent>
        </Card>
        <Card className="border border-green-200 bg-green-50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-2xl font-bold font-ibm-plex-mono text-green-600">
              {locations.filter(l => l.overall_status === "green").length}
            </p>
            <p className="text-xs text-green-600 font-ibm-plex">Conformi</p>
          </CardContent>
        </Card>
      </div>

      {/* Reports & Alerts */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="pt-5 pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-ibm-plex font-semibold text-sm text-[#09090B] mr-2">Report:</span>
            <a href={`${API_URL}/api/compliance/report/pdf?report_type=overview`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Conformità PDF</Button>
            </a>
            <a href={`${API_URL}/api/compliance/report/excel?report_type=overview`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Conformità Excel</Button>
            </a>
            <a href={`${API_URL}/api/compliance/report/pdf?report_type=scadenze`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Scadenziario PDF</Button>
            </a>
            <a href={`${API_URL}/api/compliance/report/excel?report_type=scadenze`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Scadenziario Excel</Button>
            </a>
            <a href={`${API_URL}/api/compliance/report/pdf?report_type=formazione`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Formazione PDF</Button>
            </a>
            <a href={`${API_URL}/api/compliance/report/excel?report_type=formazione`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="font-ibm-plex text-xs"><Download className="w-3 h-3 mr-1" /> Formazione Excel</Button>
            </a>
            <div className="border-l border-[#E4E4E7] h-6 mx-1" />
            <Button variant="outline" size="sm" className="font-ibm-plex text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/api/compliance/send-alerts`, {}, { withCredentials: true });
                  alert(res.data.message);
                } catch (err) {
                  alert("Errore: " + (err.response?.data?.detail || err.message));
                }
              }}
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> Invia Alert Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Locations table */}
      <Card className="border border-[#E4E4E7]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5]">
                  <TableHead className="font-ibm-plex font-semibold">Stato</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Unità Locale</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Azienda</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">Antincendio</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">Primo Socc.</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">HACCP</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">SCIA</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-center">Dip.</TableHead>
                  <TableHead className="font-ibm-plex font-semibold"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.location_id}>
                    <TableCell><StatusDot status={loc.overall_status} /></TableCell>
                    <TableCell className="font-ibm-plex font-medium">{loc.location_name}</TableCell>
                    <TableCell className="font-ibm-plex text-sm text-[#52525B]">{loc.company_name}</TableCell>
                    <TableCell className="text-center">
                      <SquadBadge data={loc.squads?.antincendio} />
                    </TableCell>
                    <TableCell className="text-center">
                      <SquadBadge data={loc.squads?.primo_soccorso} />
                    </TableCell>
                    <TableCell className="text-center">
                      <SquadBadge data={loc.squads?.haccp} />
                    </TableCell>
                    <TableCell className="text-center">
                      {loc.scia.required ? <StatusDot status={loc.scia.status} /> : <span className="text-xs text-[#A1A1AA]">N/A</span>}
                    </TableCell>
                    <TableCell className="font-ibm-plex-mono text-center text-sm">{loc.employee_count}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSelectLocation(loc.location_id)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SquadBadge({ data }) {
  if (!data || data.required === 0) return <span className="text-xs text-[#A1A1AA]">-</span>;
  const color = data.missing > 0 ? "red" : data.expiring > 0 ? "yellow" : "green";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-ibm-plex-mono ${color === "red" ? "text-red-600" : color === "yellow" ? "text-yellow-600" : "text-green-600"}`}>
      {data.valid}/{data.required}
    </span>
  );
}

// ==================== LOCATION SETTINGS TAB ====================
function LocationSettingsTab({ locationId, locations, onSaved }) {
  const [selectedLoc, setSelectedLoc] = useState(locationId || "");
  const [form, setForm] = useState({
    scia_required: false, scia_protocol: "", scia_issue_date: "", scia_expiry_date: "", scia_authority: "",
    min_antincendio: 0, min_primo_soccorso: 0, min_haccp: 0,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedLoc) {
      axios.get(`${API_URL}/api/compliance/locations/${selectedLoc}`, { withCredentials: true }).then(res => {
        setForm({
          scia_required: res.data.scia_required || false,
          scia_protocol: res.data.scia_protocol || "",
          scia_issue_date: res.data.scia_issue_date || "",
          scia_expiry_date: res.data.scia_expiry_date || "",
          scia_authority: res.data.scia_authority || "",
          min_antincendio: res.data.min_antincendio || 0,
          min_primo_soccorso: res.data.min_primo_soccorso || 0,
          min_haccp: res.data.min_haccp || 0,
        });
      }).catch(() => {});
    }
  }, [selectedLoc]);

  const handleSave = async () => {
    if (!selectedLoc) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/compliance/locations/${selectedLoc}`, {
        location_id: selectedLoc, ...form,
        min_antincendio: parseInt(form.min_antincendio) || 0,
        min_primo_soccorso: parseInt(form.min_primo_soccorso) || 0,
        min_haccp: parseInt(form.min_haccp) || 0,
      }, { withCredentials: true });
      if (onSaved) onSaved();
      alert("Impostazioni salvate");
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Select value={selectedLoc || "none"} onValueChange={(v) => setSelectedLoc(v === "none" ? "" : v)}>
        <SelectTrigger className="font-ibm-plex max-w-md" data-testid="compliance-loc-select">
          <SelectValue placeholder="Seleziona unità locale" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Seleziona...</SelectItem>
          {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {selectedLoc && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SCIA */}
          <Card className="border border-[#E4E4E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit flex items-center gap-2"><Flame className="w-4 h-4 text-orange-500" /> SCIA Antincendio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox checked={form.scia_required} onCheckedChange={(v) => setForm({...form, scia_required: v})} id="scia-req" />
                <Label htmlFor="scia-req" className="font-ibm-plex text-sm">Obbligo SCIA Antincendio</Label>
              </div>
              {form.scia_required && (
                <div className="space-y-3 pt-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">N. Protocollo</Label>
                      <Input value={form.scia_protocol} onChange={(e) => setForm({...form, scia_protocol: e.target.value})} className="font-ibm-plex" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Ente</Label>
                      <Input value={form.scia_authority} onChange={(e) => setForm({...form, scia_authority: e.target.value})} className="font-ibm-plex" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Data Rilascio</Label>
                      <Input type="date" value={form.scia_issue_date} onChange={(e) => setForm({...form, scia_issue_date: e.target.value})} className="font-ibm-plex" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Data Scadenza</Label>
                      <Input type="date" value={form.scia_expiry_date} onChange={(e) => setForm({...form, scia_expiry_date: e.target.value})} className="font-ibm-plex" />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Squadre */}
          <Card className="border border-[#E4E4E7]">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-outfit flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" /> Squadre Minime</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: "min_antincendio", label: "Addetti Antincendio", icon: Flame, color: "text-orange-500" },
                { key: "min_primo_soccorso", label: "Addetti Primo Soccorso", icon: HeartPulse, color: "text-red-500" },
                { key: "min_haccp", label: "Addetti HACCP", icon: UtensilsCrossed, color: "text-green-600" },
              ].map(({ key, label, icon: Icon, color }) => (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${color}`} />
                    <span className="font-ibm-plex text-sm">{label}</span>
                  </div>
                  <Input type="number" min="0" value={form[key]} onChange={(e) => setForm({...form, [key]: e.target.value})}
                    className="font-ibm-plex-mono w-20 h-8 text-center" />
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="lg:col-span-2">
            <Button onClick={handleSave} disabled={saving} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Impostazioni"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== SCADENZIARIO TAB ====================
function ScadenziarioTab() {
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState("90");

  const loadAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/compliance/scadenziario`, {
        params: { days_filter: parseInt(filter) }, withCredentials: true
      });
      setAlerts(res.data.alerts || []);
    } catch (err) { console.error(err); }
  }, [filter]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="font-ibm-plex text-sm text-[#52525B]">Mostra scadenze entro:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 font-ibm-plex">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Scaduti</SelectItem>
            <SelectItem value="7">7 giorni</SelectItem>
            <SelectItem value="30">30 giorni</SelectItem>
            <SelectItem value="60">60 giorni</SelectItem>
            <SelectItem value="90">90 giorni</SelectItem>
            <SelectItem value="180">180 giorni</SelectItem>
            <SelectItem value="365">1 anno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {alerts.length === 0 ? (
        <div className="text-center py-8 text-[#71717A] font-ibm-plex">
          <Shield className="w-10 h-10 mx-auto mb-2 text-[#D4D4D8]" />
          <p>Nessuna scadenza nel periodo selezionato</p>
        </div>
      ) : (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5]">
                  <TableHead className="font-ibm-plex font-semibold">Stato</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Tipo</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Dipendente</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Unità Locale</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Scadenza</TableHead>
                  <TableHead className="font-ibm-plex font-semibold text-right">Giorni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.map((a, i) => (
                  <TableRow key={i} className={a.status === "red" ? "bg-red-50/50" : a.status === "yellow" ? "bg-yellow-50/50" : ""}>
                    <TableCell><StatusDot status={a.status} /></TableCell>
                    <TableCell className="font-ibm-plex text-sm font-medium">{a.cert_type_label}</TableCell>
                    <TableCell className="font-ibm-plex text-sm">{a.employee_name || "-"}</TableCell>
                    <TableCell className="font-ibm-plex text-sm text-[#52525B]">{a.location_name}</TableCell>
                    <TableCell className="font-ibm-plex-mono text-sm">{formatDate(a.expiry_date)}</TableCell>
                    <TableCell className={`font-ibm-plex-mono text-sm text-right font-medium ${a.days_left < 0 ? "text-red-600" : a.days_left <= 30 ? "text-yellow-600" : ""}`}>
                      {a.days_left < 0 ? `${Math.abs(a.days_left)}g scaduto` : `${a.days_left}g`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== FORMAZIONE TAB ====================
function FormazioneTab({ employees, locations }) {
  const [selectedEmp, setSelectedEmp] = useState("");
  const [certs, setCerts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ cert_type: "antincendio", course_date: "", expiry_date: "", training_provider: "", notes: "" });

  const loadCerts = useCallback(async () => {
    if (!selectedEmp) { setCerts([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/compliance/certifications/${selectedEmp}`, { withCredentials: true });
      setCerts(res.data);
    } catch (err) { console.error(err); }
  }, [selectedEmp]);

  useEffect(() => { loadCerts(); }, [loadCerts]);

  const handleSave = async () => {
    try {
      const payload = { ...form, employee_id: selectedEmp };
      if (editing) {
        await axios.put(`${API_URL}/api/compliance/certifications/${editing}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/compliance/certifications`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      loadCerts();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa certificazione?")) return;
    try {
      await axios.delete(`${API_URL}/api/compliance/certifications/${id}`, { withCredentials: true });
      loadCerts();
    } catch (err) { alert("Errore"); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ cert_type: "antincendio", course_date: "", expiry_date: "", training_provider: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.id);
    setForm({ cert_type: c.cert_type, course_date: c.course_date || "", expiry_date: c.expiry_date || "", training_provider: c.training_provider || "", notes: c.notes || "" });
    setDialogOpen(true);
  };

  const activeEmps = employees.filter(e => e.is_active !== false);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedEmp || "none"} onValueChange={(v) => setSelectedEmp(v === "none" ? "" : v)}>
          <SelectTrigger className="font-ibm-plex w-72" data-testid="cert-emp-select">
            <SelectValue placeholder="Seleziona dipendente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Seleziona dipendente...</SelectItem>
            {activeEmps.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedEmp && (
          <Button onClick={openAdd} className="bg-black hover:bg-gray-800 text-white font-ibm-plex" data-testid="add-cert-btn">
            <Plus className="w-4 h-4 mr-2" /> Aggiungi Certificazione
          </Button>
        )}
      </div>

      {selectedEmp && certs.length > 0 && (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5]">
                  <TableHead className="font-ibm-plex font-semibold">Tipo</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Data Corso</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Scadenza</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Ente</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Stato</TableHead>
                  <TableHead className="font-ibm-plex font-semibold w-20">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certs.map(c => {
                  const dl = c.expiry_date ? Math.ceil((new Date(c.expiry_date) - new Date()) / 86400000) : null;
                  const status = dl === null ? "grey" : dl < 0 ? "red" : dl <= 90 ? "yellow" : "green";
                  return (
                    <TableRow key={c.id}>
                      <TableCell className="font-ibm-plex font-medium text-sm">{CERT_TYPES[c.cert_type] || c.cert_type}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm">{formatDate(c.course_date)}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm">{formatDate(c.expiry_date)}</TableCell>
                      <TableCell className="font-ibm-plex text-sm text-[#52525B]">{c.training_provider || "-"}</TableCell>
                      <TableCell>
                        <StatusBadge status={status} text={dl === null ? "N/D" : dl < 0 ? "Scaduto" : dl <= 90 ? `${dl}g` : "Valido"} />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {selectedEmp && certs.length === 0 && (
        <div className="text-center py-8 text-[#71717A] font-ibm-plex text-sm">
          <FileText className="w-10 h-10 mx-auto mb-2 text-[#D4D4D8]" />
          Nessuna certificazione registrata
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editing ? "Modifica" : "Nuova"} Certificazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Tipo</Label>
              <Select value={form.cert_type} onValueChange={(v) => setForm({...form, cert_type: v})}>
                <SelectTrigger className="font-ibm-plex"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CERT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-ibm-plex">Data Corso</Label>
                <Input type="date" value={form.course_date} onChange={(e) => setForm({...form, course_date: e.target.value})} className="font-ibm-plex" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-ibm-plex">Data Scadenza</Label>
                <Input type="date" value={form.expiry_date} onChange={(e) => setForm({...form, expiry_date: e.target.value})} className="font-ibm-plex" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Ente Formatore</Label>
              <Input value={form.training_provider} onChange={(e) => setForm({...form, training_provider: e.target.value})} className="font-ibm-plex" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Note</Label>
              <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="font-ibm-plex" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ibm-plex">Annulla</Button>
            <Button onClick={handleSave} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">{editing ? "Salva" : "Aggiungi"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== EMERGENCY TAB ====================
function EmergencyTab({ locations }) {
  const [selectedLoc, setSelectedLoc] = useState("");
  const [subTab, setSubTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [register, setRegister] = useState([]);
  const [saving, setSaving] = useState(false);

  // Procedure upload
  const [procDialogOpen, setProcDialogOpen] = useState(false);
  const [procForm, setProcForm] = useState({ procedure_type: "", description: "", file: null });

  // Register event
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventForm, setEventForm] = useState({
    event_type: "", date: "", time: "", description: "", actions_taken: "", personnel: "", outcome: "", file: null
  });

  useEffect(() => {
    if (selectedLoc) {
      axios.get(`${API_URL}/api/emergency/contacts/${selectedLoc}`, { withCredentials: true })
        .then(res => setContacts(res.data.contacts || [])).catch(() => {});
      axios.get(`${API_URL}/api/emergency/procedures/${selectedLoc}`, { withCredentials: true })
        .then(res => setProcedures(res.data)).catch(() => {});
      axios.get(`${API_URL}/api/emergency/register`, { params: { location_id: selectedLoc }, withCredentials: true })
        .then(res => setRegister(res.data)).catch(() => {});
    }
  }, [selectedLoc]);

  const saveContacts = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/emergency/contacts/${selectedLoc}`, { contacts }, { withCredentials: true });
      alert("Contatti salvati");
    } catch (err) { alert("Errore"); }
    finally { setSaving(false); }
  };

  const addContact = () => setContacts([...contacts, { role: "", number: "", name: "" }]);
  const removeContact = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const updateContact = (i, field, val) => {
    const c = [...contacts]; c[i] = { ...c[i], [field]: val }; setContacts(c);
  };

  const handleUploadProc = async () => {
    if (!procForm.file || !procForm.procedure_type) return;
    const fd = new FormData();
    fd.append("file", procForm.file);
    fd.append("location_id", selectedLoc);
    fd.append("procedure_type", procForm.procedure_type);
    fd.append("description", procForm.description);
    try {
      await axios.post(`${API_URL}/api/emergency/procedures/upload`, fd, { withCredentials: true });
      setProcDialogOpen(false);
      const res = await axios.get(`${API_URL}/api/emergency/procedures/${selectedLoc}`, { withCredentials: true });
      setProcedures(res.data);
    } catch (err) { alert("Errore"); }
  };

  const deleteProc = async (id) => {
    if (!window.confirm("Eliminare questa procedura?")) return;
    await axios.delete(`${API_URL}/api/emergency/procedures/${id}`, { withCredentials: true });
    setProcedures(procedures.filter(p => p.id !== id));
  };

  const handleCreateEvent = async () => {
    const fd = new FormData();
    fd.append("location_id", selectedLoc);
    Object.entries(eventForm).forEach(([k, v]) => { if (v && k !== "file") fd.append(k, v); });
    if (eventForm.file) fd.append("file", eventForm.file);
    try {
      await axios.post(`${API_URL}/api/emergency/register`, fd, { withCredentials: true });
      setEventDialogOpen(false);
      setEventForm({ event_type: "", date: "", time: "", description: "", actions_taken: "", personnel: "", outcome: "", file: null });
      const res = await axios.get(`${API_URL}/api/emergency/register`, { params: { location_id: selectedLoc }, withCredentials: true });
      setRegister(res.data);
    } catch (err) { alert("Errore"); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    await axios.delete(`${API_URL}/api/emergency/register/${id}`, { withCredentials: true });
    setRegister(register.filter(e => e.id !== id));
  };

  return (
    <div className="space-y-4">
      <Select value={selectedLoc || "none"} onValueChange={(v) => setSelectedLoc(v === "none" ? "" : v)}>
        <SelectTrigger className="font-ibm-plex w-64" data-testid="emergency-loc-select">
          <SelectValue placeholder="Seleziona unità locale" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Seleziona...</SelectItem>
          {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {selectedLoc && (
        <>
          {/* Sub-tabs */}
          <div className="flex gap-1 border-b border-[#E4E4E7]">
            {[
              { key: "contacts", icon: Phone, label: "Numeri Utili" },
              { key: "procedures", icon: BookOpen, label: "Procedure" },
              { key: "register", icon: FileText, label: "Registro" },
            ].map(({ key, icon: Icon, label }) => (
              <button key={key} onClick={() => setSubTab(key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-ibm-plex font-medium border-b-2 -mb-px transition-colors ${subTab === key ? "border-[#09090B] text-[#09090B]" : "border-transparent text-[#71717A] hover:text-[#52525B]"}`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </div>

          {/* Numeri Utili */}
          {subTab === "contacts" && (
            <Card className="border border-[#E4E4E7]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-outfit flex items-center gap-2"><Phone className="w-4 h-4" /> Numeri Utili</CardTitle>
                  <Button variant="outline" size="sm" onClick={addContact} className="font-ibm-plex text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Aggiungi
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {contacts.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                    <Input value={c.role} onChange={(e) => updateContact(i, "role", e.target.value)} placeholder="Ruolo/Servizio" className="font-ibm-plex text-sm h-9" />
                    <Input value={c.name} onChange={(e) => updateContact(i, "name", e.target.value)} placeholder="Nome" className="font-ibm-plex text-sm h-9" />
                    <Input value={c.number} onChange={(e) => updateContact(i, "number", e.target.value)} placeholder="Numero" className="font-ibm-plex-mono text-sm h-9" />
                    <Button variant="ghost" size="icon" className="h-9 w-9 text-red-400" onClick={() => removeContact(i)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button onClick={saveContacts} disabled={saving} className="bg-black hover:bg-gray-800 text-white font-ibm-plex mt-3">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Salva Contatti"}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Procedure Emergenza */}
          {subTab === "procedures" && (
            <div className="space-y-4">
              <Button onClick={() => { setProcForm({ procedure_type: "", description: "", file: null }); setProcDialogOpen(true); }}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
                <Upload className="w-4 h-4 mr-2" /> Carica Procedura
              </Button>

              {Object.entries(EMERGENCY_TYPES).map(([type, label]) => {
                const typeDocs = procedures.filter(p => p.procedure_type === type);
                if (typeDocs.length === 0) return null;
                return (
                  <Card key={type} className="border border-[#E4E4E7]">
                    <CardHeader className="py-3"><CardTitle className="text-sm font-outfit">{label}</CardTitle></CardHeader>
                    <CardContent className="pt-0 space-y-1">
                      {typeDocs.map(p => (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#F4F4F5] last:border-0">
                          <div className="min-w-0 flex-1">
                            <p className="font-ibm-plex text-sm truncate">{p.description || p.filename}</p>
                            <p className="text-xs text-[#71717A]">{p.filename}</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <a href={`${API_URL}/api/emergency/procedures/download/${p.id}`} target="_blank" rel="noreferrer">
                              <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="w-3.5 h-3.5" /></Button>
                            </a>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteProc(p.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}

              {procedures.length === 0 && (
                <div className="text-center py-8 text-[#71717A] font-ibm-plex text-sm">
                  <BookOpen className="w-10 h-10 mx-auto mb-2 text-[#D4D4D8]" />
                  Nessuna procedura caricata
                </div>
              )}

              <Dialog open={procDialogOpen} onOpenChange={setProcDialogOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle className="font-outfit">Carica Procedura Emergenza</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Tipo Procedura *</Label>
                      <Select value={procForm.procedure_type || "none"} onValueChange={(v) => setProcForm({...procForm, procedure_type: v === "none" ? "" : v})}>
                        <SelectTrigger className="font-ibm-plex"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seleziona...</SelectItem>
                          {Object.entries(EMERGENCY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Descrizione</Label>
                      <Input value={procForm.description} onChange={(e) => setProcForm({...procForm, description: e.target.value})} className="font-ibm-plex" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">File *</Label>
                      <Input type="file" onChange={(e) => setProcForm({...procForm, file: e.target.files[0]})} className="font-ibm-plex" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setProcDialogOpen(false)} className="font-ibm-plex">Annulla</Button>
                    <Button onClick={handleUploadProc} disabled={!procForm.file || !procForm.procedure_type} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">Carica</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Registro Emergenze */}
          {subTab === "register" && (
            <div className="space-y-4">
              <Button onClick={() => { setEventForm({ event_type: "", date: new Date().toISOString().split("T")[0], time: "", description: "", actions_taken: "", personnel: "", outcome: "", file: null }); setEventDialogOpen(true); }}
                className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
                <Plus className="w-4 h-4 mr-2" /> Registra Evento
              </Button>

              {register.length > 0 ? (
                <Card className="border border-[#E4E4E7]">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F4F4F5]">
                          <TableHead className="font-ibm-plex font-semibold">Data</TableHead>
                          <TableHead className="font-ibm-plex font-semibold">Ora</TableHead>
                          <TableHead className="font-ibm-plex font-semibold">Tipo</TableHead>
                          <TableHead className="font-ibm-plex font-semibold">Descrizione</TableHead>
                          <TableHead className="font-ibm-plex font-semibold">Esito</TableHead>
                          <TableHead className="font-ibm-plex font-semibold w-20">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {register.map(ev => (
                          <TableRow key={ev.id}>
                            <TableCell className="font-ibm-plex-mono text-sm">{formatDate(ev.date)}</TableCell>
                            <TableCell className="font-ibm-plex-mono text-sm">{ev.time || "-"}</TableCell>
                            <TableCell className="font-ibm-plex text-sm font-medium">{EMERGENCY_TYPES[ev.event_type] || ev.event_type}</TableCell>
                            <TableCell className="font-ibm-plex text-sm text-[#52525B] max-w-[200px] truncate">{ev.description}</TableCell>
                            <TableCell className="font-ibm-plex text-sm">{ev.outcome || "-"}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {ev.filename && (
                                  <a href={`${API_URL}/api/emergency/register/download/${ev.id}`} target="_blank" rel="noreferrer">
                                    <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="w-3.5 h-3.5" /></Button>
                                  </a>
                                )}
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => deleteEvent(ev.id)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <div className="text-center py-8 text-[#71717A] font-ibm-plex text-sm">
                  <Siren className="w-10 h-10 mx-auto mb-2 text-[#D4D4D8]" />
                  Nessun evento registrato
                </div>
              )}

              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle className="font-outfit">Registra Evento Emergenza</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-ibm-plex">Tipo *</Label>
                        <Select value={eventForm.event_type || "none"} onValueChange={(v) => setEventForm({...eventForm, event_type: v === "none" ? "" : v})}>
                          <SelectTrigger className="font-ibm-plex"><SelectValue placeholder="Tipo" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Seleziona...</SelectItem>
                            {Object.entries(EMERGENCY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-ibm-plex">Data *</Label>
                        <Input type="date" value={eventForm.date} onChange={(e) => setEventForm({...eventForm, date: e.target.value})} className="font-ibm-plex" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-ibm-plex">Ora</Label>
                        <Input type="time" value={eventForm.time} onChange={(e) => setEventForm({...eventForm, time: e.target.value})} className="font-ibm-plex" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Descrizione</Label>
                      <Textarea value={eventForm.description} onChange={(e) => setEventForm({...eventForm, description: e.target.value})} rows={2} className="font-ibm-plex" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Azioni Intraprese</Label>
                      <Textarea value={eventForm.actions_taken} onChange={(e) => setEventForm({...eventForm, actions_taken: e.target.value})} rows={2} className="font-ibm-plex" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-ibm-plex">Personale Coinvolto</Label>
                        <Input value={eventForm.personnel} onChange={(e) => setEventForm({...eventForm, personnel: e.target.value})} className="font-ibm-plex" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-ibm-plex">Esito</Label>
                        <Input value={eventForm.outcome} onChange={(e) => setEventForm({...eventForm, outcome: e.target.value})} className="font-ibm-plex" placeholder="Es. Risolto" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-ibm-plex">Allegato</Label>
                      <Input type="file" onChange={(e) => setEventForm({...eventForm, file: e.target.files[0]})} className="font-ibm-plex" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEventDialogOpen(false)} className="font-ibm-plex">Annulla</Button>
                    <Button onClick={handleCreateEvent} disabled={!eventForm.event_type || !eventForm.date} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">Registra</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== LOCATION DOCS TAB ====================
function LocationDocsTab({ locations }) {
  const [selectedLoc, setSelectedLoc] = useState("");
  const [selectedCat, setSelectedCat] = useState("");
  const [docs, setDocs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: "", subcategory: "", description: "", doc_date: "", expiry_date: "", notes: "", file: null
  });
  const [uploading, setUploading] = useState(false);

  const loadDocs = useCallback(async () => {
    if (!selectedLoc) { setDocs([]); return; }
    try {
      const params = { ...(selectedCat ? { category: selectedCat } : {}) };
      const res = await axios.get(`${API_URL}/api/compliance/location-documents/${selectedLoc}`, { params, withCredentials: true });
      setDocs(res.data);
    } catch (err) { console.error(err); }
  }, [selectedLoc, selectedCat]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.category) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", uploadForm.file);
    fd.append("location_id", selectedLoc);
    fd.append("category", uploadForm.category);
    fd.append("subcategory", uploadForm.subcategory);
    fd.append("description", uploadForm.description || uploadForm.file.name);
    fd.append("doc_date", uploadForm.doc_date);
    fd.append("expiry_date", uploadForm.expiry_date);
    fd.append("notes", uploadForm.notes);
    try {
      await axios.post(`${API_URL}/api/compliance/location-documents/upload`, fd, { withCredentials: true });
      setUploadOpen(false);
      setUploadForm({ category: "", subcategory: "", description: "", doc_date: "", expiry_date: "", notes: "", file: null });
      loadDocs();
    } catch (err) {
      alert("Errore: " + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await axios.delete(`${API_URL}/api/compliance/location-documents/delete/${id}`, { withCredentials: true });
      loadDocs();
    } catch (err) { alert("Errore"); }
  };

  const subcats = uploadForm.category ? (LOC_DOC_CATEGORIES[uploadForm.category]?.subcategories || []) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedLoc || "none"} onValueChange={(v) => setSelectedLoc(v === "none" ? "" : v)}>
          <SelectTrigger className="font-ibm-plex w-64" data-testid="loc-docs-select">
            <SelectValue placeholder="Seleziona unità locale" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Seleziona...</SelectItem>
            {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {selectedLoc && (
          <>
            <Select value={selectedCat || "all"} onValueChange={(v) => setSelectedCat(v === "all" ? "" : v)}>
              <SelectTrigger className="font-ibm-plex w-52">
                <SelectValue placeholder="Tutte le categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {Object.entries(LOC_DOC_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setUploadOpen(true)} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
              <Upload className="w-4 h-4 mr-2" /> Carica Documento
            </Button>
          </>
        )}
      </div>

      {selectedLoc && docs.length > 0 && (
        <Card className="border border-[#E4E4E7]">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F4F4F5]">
                  <TableHead className="font-ibm-plex font-semibold">Categoria</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Sottocategoria</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Descrizione</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Data Doc.</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Scadenza</TableHead>
                  <TableHead className="font-ibm-plex font-semibold">Stato</TableHead>
                  <TableHead className="font-ibm-plex font-semibold w-28">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map(d => {
                  const dl = d.expiry_date ? Math.ceil((new Date(d.expiry_date) - new Date()) / 86400000) : null;
                  const status = dl === null ? "grey" : dl < 0 ? "red" : dl <= 90 ? "yellow" : "green";
                  return (
                    <TableRow key={d.id}>
                      <TableCell className="font-ibm-plex text-sm font-medium">{LOC_DOC_CATEGORIES[d.category]?.label || d.category}</TableCell>
                      <TableCell className="font-ibm-plex text-sm text-[#52525B]">{d.subcategory || "-"}</TableCell>
                      <TableCell className="font-ibm-plex text-sm">{d.description}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm">{formatDate(d.doc_date)}</TableCell>
                      <TableCell className="font-ibm-plex-mono text-sm">{formatDate(d.expiry_date)}</TableCell>
                      <TableCell>
                        {dl !== null ? (
                          <StatusBadge status={status} text={dl < 0 ? "Scaduto" : dl <= 90 ? `${dl}g` : "Valido"} />
                        ) : <span className="text-xs text-[#A1A1AA]">-</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <a href={`${API_URL}/api/compliance/location-documents/download/${d.id}`} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Anteprima"><Eye className="w-3.5 h-3.5" /></Button>
                          </a>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(d.id)} title="Elimina">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {selectedLoc && docs.length === 0 && (
        <div className="text-center py-8 text-[#71717A] font-ibm-plex text-sm">
          <FolderOpen className="w-10 h-10 mx-auto mb-2 text-[#D4D4D8]" />
          Nessun documento per questa unità locale
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Carica Documento Unità Locale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Categoria *</Label>
              <Select value={uploadForm.category || "none"} onValueChange={(v) => setUploadForm({...uploadForm, category: v === "none" ? "" : v, subcategory: ""})}>
                <SelectTrigger className="font-ibm-plex"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleziona...</SelectItem>
                  {Object.entries(LOC_DOC_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {subcats.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs font-ibm-plex">Sottocategoria</Label>
                <Select value={uploadForm.subcategory || "none"} onValueChange={(v) => setUploadForm({...uploadForm, subcategory: v === "none" ? "" : v})}>
                  <SelectTrigger className="font-ibm-plex"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleziona...</SelectItem>
                    {subcats.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Descrizione</Label>
              <Input value={uploadForm.description} onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})} className="font-ibm-plex" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-ibm-plex">Data Documento</Label>
                <Input type="date" value={uploadForm.doc_date} onChange={(e) => setUploadForm({...uploadForm, doc_date: e.target.value})} className="font-ibm-plex" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-ibm-plex">Data Scadenza</Label>
                <Input type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({...uploadForm, expiry_date: e.target.value})} className="font-ibm-plex" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">Note</Label>
              <Textarea value={uploadForm.notes} onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})} rows={2} className="font-ibm-plex" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-ibm-plex">File *</Label>
              <Input type="file" accept=".pdf,.jpg,.png,.docx" onChange={(e) => setUploadForm({...uploadForm, file: e.target.files[0] || null})} className="font-ibm-plex" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="font-ibm-plex">Annulla</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadForm.file || !uploadForm.category} className="bg-black hover:bg-gray-800 text-white font-ibm-plex">
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function CompliancePage() {
  const { companies, locations, fetchCompanies, fetchLocations } = useApp();
  const [employees, setEmployees] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedLocationId, setSelectedLocationId] = useState("");

  useEffect(() => {
    fetchCompanies();
    fetchLocations();
    loadEmployees();
    loadDashboard();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`, { withCredentials: true });
      setEmployees(res.data);
    } catch (err) { console.error(err); }
  };

  const loadDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/compliance/dashboard`, { withCredentials: true });
      setDashboardData(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSelectLocation = (locId) => {
    setSelectedLocationId(locId);
    setActiveTab("settings");
  };

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div>
        <h1 className="text-3xl font-bold font-outfit tracking-tight text-[#09090B] flex items-center gap-3">
          <Shield className="w-8 h-8" />
          Compliance e Sicurezza
        </h1>
        <p className="text-[#52525B] font-ibm-plex mt-1">
          Monitoraggio conformità normativa, documentale e formativa
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-5xl grid-cols-6">
          <TabsTrigger value="dashboard" className="font-ibm-plex" data-testid="compliance-tab-dashboard">
            <Shield className="w-4 h-4 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="settings" className="font-ibm-plex" data-testid="compliance-tab-settings">
            <Flame className="w-4 h-4 mr-1.5" /> Unità Locali
          </TabsTrigger>
          <TabsTrigger value="documenti" className="font-ibm-plex" data-testid="compliance-tab-documenti">
            <FolderOpen className="w-4 h-4 mr-1.5" /> Documenti UL
          </TabsTrigger>
          <TabsTrigger value="formazione" className="font-ibm-plex" data-testid="compliance-tab-formazione">
            <Users className="w-4 h-4 mr-1.5" /> Formazione
          </TabsTrigger>
          <TabsTrigger value="emergenze" className="font-ibm-plex" data-testid="compliance-tab-emergenze">
            <Siren className="w-4 h-4 mr-1.5" /> Emergenze
          </TabsTrigger>
          <TabsTrigger value="scadenziario" className="font-ibm-plex" data-testid="compliance-tab-scadenziario">
            <Calendar className="w-4 h-4 mr-1.5" /> Scadenziario
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab data={dashboardData} onSelectLocation={handleSelectLocation} />
        </TabsContent>

        <TabsContent value="settings">
          <LocationSettingsTab locationId={selectedLocationId} locations={locations} onSaved={loadDashboard} />
        </TabsContent>

        <TabsContent value="documenti">
          <LocationDocsTab locations={locations} />
        </TabsContent>

        <TabsContent value="formazione">
          <FormazioneTab employees={employees} locations={locations} />
        </TabsContent>

        <TabsContent value="emergenze">
          <EmergencyTab locations={locations} />
        </TabsContent>

        <TabsContent value="scadenziario">
          <ScadenziarioTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
